import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Activity,
    MessageSquare,
    CheckCircle,
    Clock,
    Bot,
    ExternalLink,
    RefreshCw,
    Terminal
} from 'lucide-react';
import axios from '../../utils/axiosConfig';

export const GlobalAgentMonitor = () => {
    const [activities, setActivities] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchActivity = async () => {
        try {
            const response = await axios.get('/agents/activity?limit=30');
            setActivities(response.data);
        } catch (error) {
            console.error("Error fetching agent activity:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchActivity();
        const interval = setInterval(fetchActivity, 10000); // Refresh every 10s
        return () => clearInterval(interval);
    }, []);

    const getActivityIcon = (type) => {
        switch (type) {
            case 'COMMENT': return <MessageSquare className="w-4 h-4 text-secondary" />;
            case 'VERIFY': return <CheckCircle className="w-4 h-4 text-primary" />;
            default: return <Activity className="w-4 h-4 text-blue-400" />;
        }
    };

    if (isLoading && activities.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin mb-4" />
                <p className="text-sm font-mono uppercase">Connecting to Lobster Feed...</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
                <button
                    onClick={() => window.location.href = '/markets'}
                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                >
                    <Terminal className="w-4 h-4 text-secondary" />
                    <h3 className="text-sm font-bold uppercase tracking-wider text-white">Live Lobster Intelligence</h3>
                </button>
                <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-secondary rounded-full animate-pulse shadow-[0_0_8px_#00d4aa]" />
                    <span className="text-[10px] font-mono text-secondary font-bold">REAL-TIME FEED</span>
                </div>
            </div>

            <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                <AnimatePresence initial={false}>
                    {activities.map((activity, idx) => (
                        <motion.div
                            key={activity.timestamp + idx}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all group"
                        >
                            <div className="flex items-start gap-4">
                                <div className="p-2 rounded-lg bg-white/5 border border-white/10 flex-shrink-0">
                                    {getActivityIcon(activity.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-black text-white group-hover:text-secondary transition-colors">
                                                {activity.agentName}
                                            </span>
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-muted-foreground font-mono">
                                                {activity.type}
                                            </span>
                                        </div>
                                        <span className="text-[10px] text-muted-foreground font-mono">
                                            {new Date(activity.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                        </span>
                                    </div>
                                    <p className="text-sm text-slate-300 leading-relaxed mb-2">
                                        {activity.message}
                                    </p>
                                    <button
                                        onClick={() => window.location.href = `/markets/${activity.marketSlug}`}
                                        className="flex items-center gap-2 text-[10px] text-muted-foreground bg-black/20 p-2 rounded-lg border border-white/5 hover:bg-black/40 transition-colors w-full text-left"
                                    >
                                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                        <span className="truncate">Target: {activity.marketTitle}</span>
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>

                {activities.length === 0 && !isLoading && (
                    <div className="text-center py-12 border border-dashed border-white/10 rounded-2xl bg-white/[0.02]">
                        <Bot className="w-12 h-12 text-white/10 mx-auto mb-4" />
                        <p className="text-muted-foreground text-sm font-mono">Awaiting initial Lobster telemetry...</p>
                    </div>
                )}
            </div>
        </div>
    );
};
